import { BellRing, CheckCircle2, Plus, RadioTower, Save, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  acknowledgeAlert,
  deleteAlertPolicy,
  getAlertPolicies,
  getAlerts,
  getPlans,
  getTelegramSettings,
  saveAlertPolicy,
  sendTestAlert
} from "../../data/api";
import type { AlertEvent, AlertPolicy, RulePlanBundle } from "../../types";

function newPolicy(plan?: RulePlanBundle): AlertPolicy {
  return {
    id: `policy-${crypto.randomUUID()}`,
    planId: plan?.id,
    name: plan ? `${plan.name} ladder alerts` : "Plan ladder alerts",
    severity: "high",
    cooldownMinutes: 30,
    dedupeScope: "step",
    marketHoursOnly: false,
    requireAck: true,
    enabled: true,
    notifyTrigger: true,
    notifySteps: true,
    selectedStepIds: plan?.steps.map((step) => step.id) ?? [],
    repeatMode: "recross",
    template: "[CP Dashboard] {{planName}} / {{stepLabel}} reached. Current {{price}}, planned {{level}}."
  };
}

export default function Alerts() {
  const [plans, setPlans] = useState<RulePlanBundle[]>([]);
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [policies, setPolicies] = useState<AlertPolicy[]>([]);
  const [telegram, setTelegram] = useState<{ configured: boolean; chatIdMasked: string | null }>({ configured: false, chatIdMasked: null });
  const [policyDraft, setPolicyDraft] = useState<AlertPolicy>(() => newPolicy());
  const [testStepId, setTestStepId] = useState("");
  const [testMessage, setTestMessage] = useState("[CP Dashboard] Telegram delivery test.");
  const [status, setStatus] = useState("Loading alert operations...");

  useEffect(() => {
    let mounted = true;
    Promise.all([
      getPlans(),
      getAlerts(),
      getTelegramSettings().catch(() => ({ configured: false, chatIdMasked: null })),
      getAlertPolicies().catch(() => [])
    ]).then(([planData, alertData, telegramData, policyData]) => {
      if (!mounted) return;
      const initialPlan = planData.find((plan) => plan.status === "active") ?? planData[0];
      const initialPolicy = policyData.find((policy) => policy.planId === initialPlan?.id) ?? policyData[0] ?? newPolicy(initialPlan);
      setPlans(planData);
      setAlerts(alertData);
      setTelegram(telegramData);
      setPolicies(policyData);
      setPolicyDraft(initialPolicy);
      setTestStepId(initialPlan?.steps[0]?.id ?? "");
      setStatus("Alert operations ready");
    }).catch(() => {
      if (mounted) setStatus("Alert operations could not be loaded");
    });
    return () => { mounted = false; };
  }, []);

  const selectedPlan = plans.find((plan) => plan.id === policyDraft.planId) ?? plans[0];
  const selectedPolicy = policies.find((policy) => policy.id === policyDraft.id);
  const effectiveSelectedSteps = useMemo(() => {
    if (!selectedPlan) return new Set<string>();
    if (policyDraft.selectedStepIds.length) return new Set(policyDraft.selectedStepIds);
    return new Set(selectedPlan.steps.filter((_, index) => index === 0 ? policyDraft.notifyTrigger : policyDraft.notifySteps).map((step) => step.id));
  }, [policyDraft.notifySteps, policyDraft.notifyTrigger, policyDraft.selectedStepIds, selectedPlan]);

  const choosePlan = (planId: string) => {
    const plan = plans.find((item) => item.id === planId);
    const existing = policies.find((policy) => policy.planId === planId);
    setPolicyDraft(existing ?? newPolicy(plan));
    setTestStepId(plan?.steps[0]?.id ?? "");
  };

  const choosePolicy = (policyId: string) => {
    const policy = policies.find((item) => item.id === policyId);
    if (!policy) return;
    setPolicyDraft(policy);
    const plan = plans.find((item) => item.id === policy.planId);
    setTestStepId(plan?.steps[0]?.id ?? "");
  };

  const toggleStep = (stepId: string) => {
    if (!selectedPlan) return;
    const selected = new Set(effectiveSelectedSteps);
    if (selected.has(stepId)) selected.delete(stepId); else selected.add(stepId);
    const triggerId = selectedPlan.steps[0]?.id;
    const nonTriggerIds = selectedPlan.steps.slice(1).map((step) => step.id);
    setPolicyDraft((current) => ({
      ...current,
      selectedStepIds: Array.from(selected),
      notifyTrigger: triggerId ? selected.has(triggerId) : false,
      notifySteps: nonTriggerIds.some((id) => selected.has(id))
    }));
  };

  const persistPolicy = async () => {
    if (!policyDraft.planId) return;
    setStatus("Saving alert policy...");
    try {
      const next = await saveAlertPolicy(policyDraft);
      const saved = next.find((policy) => policy.id === policyDraft.id) ?? policyDraft;
      setPolicies(next);
      setPolicyDraft(saved);
      setStatus("Alert policy saved to server");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Alert policy save failed");
    }
  };

  const removePolicy = async () => {
    if (!selectedPolicy) return;
    setStatus("Deleting alert policy...");
    try {
      const next = await deleteAlertPolicy(selectedPolicy.id);
      setPolicies(next);
      setPolicyDraft(next[0] ?? newPolicy(selectedPlan));
      setStatus("Alert policy deleted");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Alert policy delete failed");
    }
  };

  const testSend = async () => {
    const cooldownUntil = new Date(Date.now() + policyDraft.cooldownMinutes * 60_000).toISOString();
    const dedupeKey = `${policyDraft.planId || "global"}:${testStepId || "manual"}:test`;
    setStatus("Sending Telegram test alert...");
    try {
      const payload = await sendTestAlert({
        planId: policyDraft.planId,
        stepId: testStepId,
        severity: policyDraft.severity,
        dedupeKey,
        cooldownUntil,
        message: testMessage
      });
      if (payload.alerts) setAlerts(payload.alerts);
      setStatus(payload.sent ? "Telegram test sent" : "Alert recorded");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Telegram test failed");
      setAlerts(await getAlerts());
    }
  };

  const ack = async (id: string) => {
    setStatus("Acknowledging alert...");
    try {
      setAlerts(await acknowledgeAlert(id));
      setStatus("Alert acknowledged");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Acknowledgement failed");
    }
  };

  return (
    <section className="route-page alerts-page">
      <div className="page-head compact-head">
        <div>
          <p className="eyebrow">Alert Operations</p>
          <h2>Rule crossing detection and Telegram delivery</h2>
          <span>Daily close and manual refresh evaluation, with push ingestion ready for a licensed real-time source.</span>
        </div>
        <div className="head-actions">
          <span className={`status-pill ${telegram.configured ? "" : "warn"}`}>{telegram.configured ? `Telegram ${telegram.chatIdMasked}` : "Telegram not configured"}</span>
          <span className="status-pill warn">{status}</span>
        </div>
      </div>

      <div className="alert-cadence-strip">
        <Policy label="Scheduled Evaluation" value="Daily 22:30 UTC" />
        <Policy label="On-demand Evaluation" value="Dashboard Refresh" />
        <Policy label="Provider-safe Alternative" value="Authenticated Push Ingest" />
      </div>

      <div className="alerts-layout">
        <section className="settings-panel alert-policy-editor">
          <div className="panel-section-title"><RadioTower /> Automatic Signal Policy</div>
          <div className="alert-policy-toolbar">
            <label className="field"><span>Plan</span><select value={policyDraft.planId ?? ""} onChange={(event) => choosePlan(event.target.value)}>{plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}</select></label>
            <label className="field"><span>Saved Policy</span><select value={selectedPolicy?.id ?? ""} onChange={(event) => choosePolicy(event.target.value)}><option value="">New policy</option>{policies.map((policy) => <option key={policy.id} value={policy.id}>{policy.name}</option>)}</select></label>
            <button className="btn" type="button" onClick={() => setPolicyDraft(newPolicy(selectedPlan))}><Plus /> New</button>
            <button className="btn danger" type="button" onClick={() => void removePolicy()} disabled={!selectedPolicy}><Trash2 /> Delete</button>
          </div>

          <div className="alert-policy-options">
            <label className="field policy-name-field"><span>Policy Name</span><input value={policyDraft.name} onChange={(event) => setPolicyDraft((current) => ({ ...current, name: event.target.value }))} /></label>
            <label className="field"><span>Severity</span><select value={policyDraft.severity} onChange={(event) => setPolicyDraft((current) => ({ ...current, severity: event.target.value }))}><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></label>
            <label className="field"><span>Cooldown</span><input min="0" max="10080" type="number" value={policyDraft.cooldownMinutes} onChange={(event) => setPolicyDraft((current) => ({ ...current, cooldownMinutes: Math.max(0, Number(event.target.value) || 0) }))} /><em>min</em></label>
            <label className="field"><span>Repeat</span><select value={policyDraft.repeatMode} onChange={(event) => setPolicyDraft((current) => ({ ...current, repeatMode: event.target.value as AlertPolicy["repeatMode"] }))}><option value="recross">After retreat + recross</option><option value="once">Once per step</option></select></label>
            <label className="field"><span>Dedupe Scope</span><select value={policyDraft.dedupeScope} onChange={(event) => setPolicyDraft((current) => ({ ...current, dedupeScope: event.target.value as AlertPolicy["dedupeScope"] }))}><option value="step">Step</option><option value="plan">Plan</option><option value="instrument">Instrument</option><option value="global">Global</option></select></label>
          </div>

          <div className="alert-toggle-grid">
            <Toggle label="Policy Enabled" on={policyDraft.enabled} onClick={() => setPolicyDraft((current) => ({ ...current, enabled: !current.enabled }))} />
            <Toggle label="Require ACK" on={policyDraft.requireAck} onClick={() => setPolicyDraft((current) => ({ ...current, requireAck: !current.requireAck }))} />
            <Toggle label="Market Hours Only" on={policyDraft.marketHoursOnly} onClick={() => setPolicyDraft((current) => ({ ...current, marketHoursOnly: !current.marketHoursOnly }))} />
          </div>

          <div className="alert-step-targets">
            <div className="section-title"><span>Signal Steps</span><em>{effectiveSelectedSteps.size} selected</em></div>
            <div className="alert-step-grid">
              {selectedPlan?.steps.map((step, index) => <Toggle key={step.id} label={index === 0 ? "Trigger / Step 1" : step.label} detail={`${step.dropPct.toFixed(1)}% / ${step.indexLevel.toLocaleString("en-US", { maximumFractionDigits: 2 })}`} on={effectiveSelectedSteps.has(step.id)} onClick={() => toggleStep(step.id)} />)}
            </div>
          </div>

          <label className="field message-field"><span>Telegram Template</span><textarea value={policyDraft.template} onChange={(event) => setPolicyDraft((current) => ({ ...current, template: event.target.value }))} /></label>
          <div className="template-token-row"><span>{"{{planName}}"}</span><span>{"{{stepLabel}}"}</span><span>{"{{price}}"}</span><span>{"{{level}}"}</span><span>{"{{instrument}}"}</span><span>{"{{asOf}}"}</span></div>
          <button className="btn primary full-btn" type="button" onClick={() => void persistPolicy()}><Save /> Save Automatic Policy</button>

          <div className="alert-test-block">
            <div className="panel-section-title"><BellRing /> Test Delivery</div>
            <div className="alert-test-row">
              <label className="field"><span>Step</span><select value={testStepId} onChange={(event) => setTestStepId(event.target.value)}>{selectedPlan?.steps.map((step) => <option key={step.id} value={step.id}>{step.label}</option>)}</select></label>
              <label className="field test-message-field"><span>Message</span><input value={testMessage} onChange={(event) => setTestMessage(event.target.value)} /></label>
              <button className="btn" type="button" onClick={() => void testSend()}><Send /> Test</button>
            </div>
          </div>
        </section>

        <section className="settings-panel alert-log-panel">
          <div className="panel-section-title"><CheckCircle2 /> Alert Event Log</div>
          <div className="alert-log">
            {alerts.map((alert) => (
              <article className="alert-row" key={alert.id}>
                <div><b>{alert.severity.toUpperCase()} / {alert.status}</b><span>{alert.telegramPreview}</span><em>{alert.dedupeKey} / {alert.createdAt ? new Date(alert.createdAt).toLocaleString() : "local"}</em></div>
                {alert.status !== "acknowledged" && <button className="btn compact-btn" onClick={() => void ack(alert.id)}>Ack</button>}
              </article>
            ))}
            {!alerts.length && <div className="ledger-loading">No alert events have been recorded.</div>}
          </div>
        </section>
      </div>
    </section>
  );
}

function Toggle({ label, detail, on, onClick }: { label: string; detail?: string; on: boolean; onClick: () => void }) {
  return <button className={`alert-toggle ${on ? "is-active" : ""}`} type="button" onClick={onClick}><span className={`box ${on ? "on" : ""}`} /><b>{label}</b>{detail && <em>{detail}</em>}</button>;
}

function Policy({ label, value }: { label: string; value: string }) {
  return <div className="metric-pill"><span>{label}</span><b>{value}</b></div>;
}
